import React, { FC, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { toast } from 'react-toastify';

import { ITimesheetAttachmentDTO } from 'src/api/dtos';
import CommonTimesheetRepository from 'src/api/repositories/common/v1/timesheet.repository';
import { useTimesheetData } from 'src/features/timesheets/providers';
import { DeleteIcon, Select, FileChip, WarningFilledIcon } from 'src/shared/components';
import { PermissionsEnums } from 'src/shared/enums';
import { useEditingMemoInput, usePermissions } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { saveAttachment } from 'src/shared/utils';

const NOTE_MAX_LENGTH = 512;

interface Props {
  title: string | null;
  timeCategoryId: number;
  memoAmount: number;
  attachment: ITimesheetAttachmentDTO | null | undefined;
  onDelete: () => void;
  onMemoAmountChange: (memoAmount: number) => void;
  onTimeCategoryChange: (timeCategoryId: number) => void;
  note?: string;
  onNoteChange?: (note: string) => void;
  onDeleteAttachment: (attachment: ITimesheetAttachmentDTO) => void;
}

export const TimesheetExpense: FC<Props> = ({
  title,
  timeCategoryId,
  memoAmount,
  note,
  attachment,
  onDelete,
  onMemoAmountChange,
  onTimeCategoryChange,
  onDeleteAttachment,
  onNoteChange,
}) => {
  const { t } = useTranslate();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const hasExpenseAttachmentPerm = usePermissions(PermissionsEnums.WTS_EXPENSE_ATTACHMENTS);

  const { timeCategories, loadingTimeCategories } = useTimesheetData();

  const {
    value: memoValue,
    onInputChange: onMemoChange,
    onInputBlur: onMemoBlur,
  } = useEditingMemoInput(memoAmount, (memoAmount) => onMemoAmountChange(memoAmount ?? 0));

  const handleTimeCategoryChange = (event: SelectChangeEvent) => {
    onTimeCategoryChange(Number(event.target.value));
  };

  const handleDownloadFile = async () => {
    if (!attachment?.referenceId) {
      return;
    }

    try {
      setIsDownloading(true);
      const { stream, fileName } = await CommonTimesheetRepository.downloadAttachment({
        referenceId: attachment.referenceId,
      });
      if (!stream) {
        toast.info(t('global.unableToDownload'), { type: toast.TYPE.ERROR });
        return;
      }
      if (stream) {
        saveAttachment(stream, attachment.fileName || fileName || '');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsDownloading(false);
    }
  };

  const noteMaxLength = (note?.length ?? 0) >= NOTE_MAX_LENGTH;
  const hasAttachment = attachment?.referenceId;
  const canDeleteExpense = (hasAttachment && hasExpenseAttachmentPerm) || (!hasAttachment && !hasExpenseAttachmentPerm);

  return (
    <Stack gap={2}>
      <Stack gap={1}>
        <Typography fontSize="0.875rem" fontWeight="bold" color={(theme) => theme.palette.primary.main}>
          {title}
        </Typography>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography fontSize="0.875rem" fontWeight="bold">
            {t('global.amount')} *
          </Typography>
          {canDeleteExpense && (
            <IconButton color="error" onClick={onDelete}>
              <DeleteIcon size={20} />
            </IconButton>
          )}
        </Stack>
        <TextField value={memoValue} sx={{ width: 300 }} onChange={onMemoChange} onBlur={onMemoBlur} />
      </Stack>
      <Stack gap={1}>
        <Typography fontSize="0.875rem" fontWeight="bold">
          {t('global.category')} *
        </Typography>
        <Select
          value={timeCategoryId}
          sx={{ width: 300, height: 40 }}
          disabled={loadingTimeCategories}
          onChange={handleTimeCategoryChange}
        >
          {timeCategories.map((timeCategory) => (
            <MenuItem key={timeCategory.id} value={timeCategory.id}>
              {timeCategory.description}
            </MenuItem>
          ))}
        </Select>
      </Stack>
      <Stack direction="column" gap={1}>
        <Typography fontSize="0.875rem" fontWeight="bold">
          {t('global.note')}
        </Typography>
        <Stack direction="column" gap={0.5}>
          <Stack direction="row" gap={1} alignItems="flex-start">
            <TextField
              value={note}
              onChange={(event) => onNoteChange?.(event.target.value)}
              error={noteMaxLength}
              placeholder={t('global.haveSomethingToSayAddItHere')}
              multiline
              sx={{ flex: 1 }}
            />
            <IconButton onClick={() => onNoteChange?.('')} color="error">
              <DeleteIcon size={20} />
            </IconButton>
          </Stack>
          <Typography
            fontSize={12}
            color={(theme) => (noteMaxLength ? theme.palette.error.main : theme.palette.action.active)}
            sx={{ ml: 'auto', mr: 6, display: 'flex', alignItems: 'center', gap: 1 }}
          >
            {noteMaxLength ? (
              <>
                <WarningFilledIcon size={16} />
                {t('global.noteHasReachedCharacterLimit')}
              </>
            ) : (
              `${note?.length}/${NOTE_MAX_LENGTH}`
            )}
          </Typography>
        </Stack>
      </Stack>
      {attachment?.contentType && (
        <Stack direction="column" gap={1} mt={-4}>
          <Typography fontSize="0.875rem" fontWeight="bold">
            {t('files.attachment')}
          </Typography>
          <FileChip
            loading={false}
            fileName={attachment.fileName ?? undefined}
            fileSizeInBytes={attachment.fileSizeInBytes ?? undefined}
            hasThumbnail
            disableDelete={isDownloading}
            isDownloading={isDownloading}
            file={attachment.file || undefined}
            onDeleteFile={hasExpenseAttachmentPerm ? () => onDeleteAttachment(attachment) : undefined}
            onDownloadFile={attachment?.canDownload ? undefined : handleDownloadFile}
          />
        </Stack>
      )}
    </Stack>
  );
};
